import { useRef, useState, useEffect } from "react";

import { analyticsId, getSegmentAnonymousId } from "../../utils/analytics";
import { loadSplitClient } from "./loadSplitClient";
import SplitIO from "@splitsoftware/splitio/types/splitio";
import { featureEnabled, logDev } from "utils/env";
import { useAuthContext } from "graphql/AuthContext";
import { TrafficType } from "./types";

interface ClientData extends Record<TrafficType, SplitIO.IClient | undefined> {
  manager?: SplitIO.IManager;
}

export default function useSplitIO() {
  const { currentUser } = useAuthContext();

  const clients = useRef<ClientData>({
    manager: undefined,
    [TrafficType.Anonymous]: undefined,
    [TrafficType.User]: undefined,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // if you change the name of this killswitch, please update corresponding test
    const killswitch = featureEnabled("KILLSWITCH_SPLIT");
    if (killswitch) {
      setLoading(false);
      return;
    }

    const getLoggedInClient = async function () {
      if (!currentUser) {
        return null;
      }

      // Ensure split key matches analytics key (otherwise split won't be able
      // to match segment events against the given key, which would break split
      // metrics).
      return await loadSplitClient(
        analyticsId(currentUser.id),
        TrafficType.User,
      );
    };

    const getClients = async function () {
      const [anonClientData, loggedinClientData] = await Promise.all([
        loadSplitClient(getSegmentAnonymousId(), TrafficType.Anonymous),
        getLoggedInClient(),
      ]);

      clients.current = {
        manager: anonClientData.manager, // doesn't matter which manager we use
        [TrafficType.Anonymous]: anonClientData.client,
        [TrafficType.User]: loggedinClientData?.client,
      };

      setLoading(false);
    };

    getClients();

    // We only want to execute this cleanup logic on unmount.
    return () => {
      logDev("SPLIT CLIENT UNMOUNT");
      // The split documentation recommends always doing this before
      // navigating away from the page (hard refresh) in order to make sure
      // impressions have been sent to Split.

      /* await ?? */ clients.current?.anonymous?.destroy();
      /* await ?? */ clients.current?.user?.destroy();
    };
  }, [currentUser]);

  return {
    loading,
    ...clients.current,
  };
}
